import { useEffect, useRef, useState, type RefObject } from "react";

interface UseInViewResult<T extends HTMLElement> {
  ref: RefObject<T>;
  isInView: boolean;
}

/**
 * Detecta quando um elemento entra na viewport (scroll reveal).
 * Dispara uma única vez (observer é desconectado no primeiro intersect).
 * Respeita prefers-reduced-motion: retorna visível=true de imediato, sem
 * depender do usuário rolar a página para ver o conteúdo.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(): UseInViewResult<T> {
  const ref = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setIsInView(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -80px 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, isInView };
}
